import { LogLevel, type Configuration } from '@azure/msal-browser'

/**
 * MSAL Browser configuration.
 *
 * Security-relevant choices (per ADR-008, ADR-014, and `07a-security-design.md`):
 *
 *   - `cacheLocation: 'memoryStorage'`: access token and account state live
 *     in JS memory only. No `localStorage`, no `sessionStorage`. Tab close
 *     ends the SPA session; F5 forces silent re-auth. XSS exfil window is
 *     bounded to "JS running right now"; a stored-on-disk copy never exists.
 *
 *   - `storeAuthStateInCookie: true`: REQUIRED for the redirect flow when
 *     `cacheLocation` is `memoryStorage`. The full-page navigation to
 *     `login.microsoftonline.com` and back wipes in-memory state, so MSAL
 *     must persist the transient auth-flow state (request `state`, `nonce`,
 *     PKCE `code_verifier`) in short-lived secure cookies that it deletes
 *     once `handleRedirectPromise` processes the response. Tokens are NOT
 *     stored in cookies - they remain in JS memory only (see `cacheLocation`
 *     above), so the ADR-014 "no tokens on disk" property still holds. With
 *     `false`, `loginRedirect` throws `BrowserConfigurationAuthError:
 *     in_mem_redirect_unavailable` before any redirect happens. The Entra
 *     refresh-token cookie (HttpOnly, Microsoft-managed) remains under
 *     `login.microsoftonline.com`, out of the SPA's reach.
 *
 *   - `navigateToLoginRequestUrl: false`: we handle post-redirect routing
 *     ourselves in `pages/sign-in-callback.tsx` (restore `returnTo` from
 *     the MSAL state param) rather than letting MSAL replay the original URL.
 *
 *   - PKCE with `S256` (default in @azure/msal-browser 3.x). Implicit flow
 *     is never used.
 *
 *   - `allowNativeBroker: false`: WAM brokers are a Windows-only feature
 *     and not relevant for a browser-only deployment.
 *
 * CSP-relevant notes (mirrored in ADR-015 / `07a` Section 2):
 *
 *   - MSAL injects a hidden iframe pointed at `login.microsoftonline.com`
 *     for silent token refresh. The deployed CSP MUST include
 *     `frame-src https://login.microsoftonline.com` and
 *     `connect-src https://login.microsoftonline.com`.
 *
 *   - MSAL emits no inline `<script>` tags. `script-src 'self' 'sha256-...'`
 *     (hash-based) is sufficient.
 *
 *   - `system.iframeHashTimeout` is set conservatively so the silent
 *     refresh fails fast on Conditional-Access challenge, surfacing the
 *     `SessionExpiredDialog` rather than hanging the user.
 *
 * Production values come from Vite env (`.env.example`). At test time, the
 * `__tests__` harness instantiates its own `PublicClientApplication` with a
 * stubbed account list - this config is never sent to a real IdP in tests.
 */

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '00000000-0000-0000-0000-000000000000'
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '00000000-0000-0000-0000-000000000000'
const authority =
  import.meta.env.VITE_ENTRA_AUTHORITY ?? `https://login.microsoftonline.com/${tenantId}`
const redirectUri =
  import.meta.env.VITE_ENTRA_REDIRECT_URI ?? 'http://localhost:5173/sign-in/callback'

/** The audience the SPA requests an access token for. The BFF's APIGW
 *  native JWT authoriser pins this in `aud`. Per ADR-008 and `07a` 3a. */
export const apiAudience = import.meta.env.VITE_ENTRA_AUDIENCE ?? 'api://murmuration'

/** The BFF API base URL. PR-E1 wires fetch against this. */
export const apiBaseUrl =
  import.meta.env.VITE_BFF_API_URL ?? 'https://murmuration-api.suitsmecard.com'

/**
 * Scopes requested at sign-in and at silent-refresh. `openid` + `profile`
 * yield the id_token claims; `offline_access` keeps the refresh-cookie
 * alive on the IdP side; the audience scope yields an access token the
 * BFF will accept.
 */
export const loginScopes: string[] = [
  'openid',
  'profile',
  'offline_access',
  `${apiAudience}/.default`,
]

/**
 * Scopes requested when acquiring a token for the BFF specifically.
 * `acquireTokenSilent` requires audience-bound scopes; do NOT include
 * `openid` here (it is an id-token scope, not an access-token scope).
 */
export const apiTokenScopes: string[] = [`${apiAudience}/.default`]

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri,
    postLogoutRedirectUri: '/sign-in',
    navigateToLoginRequestUrl: false,
    // Single-tenant: reject tokens from other tenants outright.
    knownAuthorities: [],
  },
  cache: {
    cacheLocation: 'memoryStorage',
    // Required for redirect flow with memoryStorage; stores only transient
    // auth-flow state (not tokens) in short-lived cookies. See header comment.
    storeAuthStateInCookie: true,
  },
  system: {
    allowNativeBroker: false,
    // Conservative iframe timeouts so silent refresh fails fast when CA
    // blocks the prompt=none round-trip, surfacing the re-auth modal.
    iframeHashTimeout: 6000,
    loadFrameTimeout: 6000,
    loggerOptions: {
      // MSAL never logs the token itself. We do not subscribe to log piiLoggingEnabled.
      piiLoggingEnabled: false,
      logLevel: LogLevel.Warning,
      loggerCallback: () => {
        // Intentional no-op. App-level logs flow through CloudWatch / RUM;
        // MSAL internal logs are not forwarded to avoid duplicating events.
      },
    },
  },
}
